import axiosInstance from "../../../services/axiosInstance";
import apiEndPoints from "../../../services/apiEndPoints";
import { setWorkspaceCurrency } from "./shared";

// Thin wrapper over the complaint endpoints. Every screen goes through here so
// URL shapes live in one place and components deal in plain data.

const unwrap = (res) => res.data?.data;

// -- metadata --------------------------------------------------------------

/**
 * Loads form metadata and, as a side effect, tells the formatter which currency
 * this workspace uses — so every amount on the screen renders correctly without
 * each component having to know.
 */
export const fetchFormMeta = () =>
  axiosInstance.get(apiEndPoints.complaintFormMeta).then((res) => {
    const data = res.data?.data;
    setWorkspaceCurrency(data?.currency);
    return data;
  });

export const fetchSettings = () =>
  axiosInstance.get(apiEndPoints.complaintSettings).then((res) => {
    const data = res.data?.data;
    setWorkspaceCurrency(data?.currency);
    return data;
  });

export const updateSettings = (payload) =>
  axiosInstance.put(apiEndPoints.complaintSettings, payload).then((res) => {
    setWorkspaceCurrency(res.data?.data?.currency);
    return res.data;
  });

export const fetchMyPermissions = () =>
  axiosInstance.get(apiEndPoints.complaintMyPermissions).then(unwrap);

export const fetchRoleCatalogueAndSettings = () =>
  Promise.all([fetchRoleCatalogue(), fetchSettings()]);

export const fetchRoleCatalogue = () =>
  axiosInstance.get(apiEndPoints.complaintRoleCatalogue).then(unwrap);

// -- complaints ------------------------------------------------------------
export const fetchComplaints = (params) =>
  axiosInstance.get(apiEndPoints.complaints, { params }).then((res) => ({
    rows: res.data?.data || [],
    pagination: res.data?.pagination || {},
  }));

export const fetchComplaint = (id) =>
  axiosInstance.get(`${apiEndPoints.complaints}/${id}`).then(unwrap);

export const createComplaint = (payload) =>
  axiosInstance.post(apiEndPoints.complaints, payload).then((res) => res.data);

export const updateComplaint = (id, payload) =>
  axiosInstance.put(`${apiEndPoints.complaints}/${id}`, payload).then((res) => res.data);

export const deleteComplaint = (id) =>
  axiosInstance.delete(`${apiEndPoints.complaints}/${id}`).then((res) => res.data);

export const changeStatus = (id, payload) =>
  axiosInstance.patch(`${apiEndPoints.complaints}/${id}/status`, payload).then((res) => res.data);

export const assignComplaint = (id, memberId) =>
  axiosInstance.patch(`${apiEndPoints.complaints}/${id}/assign`, { memberId }).then((res) => res.data);

export const forwardComplaint = (id, payload) =>
  axiosInstance.patch(`${apiEndPoints.complaints}/${id}/forward`, payload).then((res) => res.data);

export const modifySla = (id, payload) =>
  axiosInstance.patch(`${apiEndPoints.complaints}/${id}/sla`, payload).then((res) => res.data);

export const addInternalNote = (id, note) =>
  axiosInstance.post(`${apiEndPoints.complaints}/${id}/notes`, { note }).then((res) => res.data);

export const logCommunication = (id, payload) =>
  axiosInstance.post(`${apiEndPoints.complaints}/${id}/communication`, payload).then((res) => res.data);

export const uploadAttachments = (id, files) => {
  const form = new FormData();
  Array.from(files).forEach((file) => form.append("files", file));
  return axiosInstance
    .post(`${apiEndPoints.complaints}/${id}/attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((res) => res.data);
};

/**
 * Attachments sit behind auth, so they cannot be a plain <a href>. Fetch the
 * bytes with the token attached, then hand the browser a blob URL.
 */
export const openAttachment = async (id, attachment) => {
  const res = await axiosInstance.get(
    `${apiEndPoints.complaints}/${id}/attachments/${attachment._id}`,
    { responseType: "blob" }
  );

  const url = URL.createObjectURL(
    new Blob([res.data], { type: attachment.mimeType || "application/octet-stream" })
  );

  // Images and PDFs are worth previewing; anything else is saved to disk.
  const previewable = /^(image\/|video\/|application\/pdf)/.test(attachment.mimeType || "");
  if (previewable) {
    window.open(url, "_blank", "noopener,noreferrer");
  } else {
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.originalName || attachment.fileName;
    link.click();
  }

  // Give the new tab time to read the blob before the URL is revoked.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

export const setResolution = (id, payload) =>
  axiosInstance.patch(`${apiEndPoints.complaints}/${id}/resolution`, payload).then((res) => res.data);

export const reviewRefund = (id, payload) =>
  axiosInstance.patch(`${apiEndPoints.complaints}/${id}/refund/review`, payload).then((res) => res.data);

export const processRefund = (id) =>
  axiosInstance.patch(`${apiEndPoints.complaints}/${id}/refund/process`).then((res) => res.data);

export const submitFeedback = (id, payload) =>
  axiosInstance.patch(`${apiEndPoints.complaints}/${id}/feedback`, payload).then((res) => res.data);

export const submitQaScore = (id, payload) =>
  axiosInstance.patch(`${apiEndPoints.complaints}/${id}/qa`, payload).then((res) => res.data);

// -- lookups ---------------------------------------------------------------
export const lookupOrder = (orderId) =>
  axiosInstance
    .get(`${apiEndPoints.complaintOrderLookup}/${encodeURIComponent(orderId)}`)
    .then(unwrap);

export const fetchCustomerHistory = (params) =>
  axiosInstance.get(apiEndPoints.complaintCustomerHistory, { params }).then(unwrap);

// -- categories ------------------------------------------------------------
export const fetchCategories = (includeInactive = false) =>
  axiosInstance
    .get(apiEndPoints.complaintCategories, { params: { includeInactive } })
    .then(unwrap);

export const seedCategories = () =>
  axiosInstance.post(apiEndPoints.complaintCategorySeed).then((res) => res.data);

export const createCategory = (payload) =>
  axiosInstance.post(apiEndPoints.complaintCategories, payload).then((res) => res.data);

export const updateCategory = (id, payload) =>
  axiosInstance.put(`${apiEndPoints.complaintCategories}/${id}`, payload).then((res) => res.data);

export const deleteCategory = (id) =>
  axiosInstance.delete(`${apiEndPoints.complaintCategories}/${id}`).then((res) => res.data);

export const addSubCategory = (id, name) =>
  axiosInstance
    .post(`${apiEndPoints.complaintCategories}/${id}/sub-category`, { name })
    .then((res) => res.data);

export const removeSubCategory = (id, name) =>
  axiosInstance
    .delete(`${apiEndPoints.complaintCategories}/${id}/sub-category/${encodeURIComponent(name)}`)
    .then((res) => res.data);

// -- roles -----------------------------------------------------------------
export const fetchRoleAssignments = () =>
  axiosInstance.get(apiEndPoints.complaintRoleAssignments).then(unwrap);

export const fetchAssignableMembers = () =>
  axiosInstance.get(apiEndPoints.complaintAssignableMembers).then(unwrap);

export const assignRole = (payload) =>
  axiosInstance.post(apiEndPoints.complaintAssignRole, payload).then((res) => res.data);

export const revokeRole = (id) =>
  axiosInstance.delete(`${apiEndPoints.complaintRoleAssignments}/${id}`).then((res) => res.data);

// -- reports ---------------------------------------------------------------
export const fetchReport = (name, params) =>
  axiosInstance.get(`${apiEndPoints.complaintReports}/${name}`, { params }).then(unwrap);

/** Message from an API error, falling back to something readable. */
export const errorMessage = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message || err?.message || fallback;
